import React, { useState } from "react";
import { TextInput } from "../Shared/Inputs/TextInput";
import { TextAreaInput } from "../Shared/Inputs/TextAreaInput";
import { FileInput } from "../Shared/Inputs/FileInput";
import { RecruiterContactModel, RecruiterContactValidation } from "../../models/RecruiterContactModel";
import { Button } from "../Shared/Buttons/Button";
import { postRecruiterContactRequest } from "../../services/HomeService";
import { useAlert } from "../../hooks/useAlert";
import useErrorPage from "../../hooks/useErrorPage";
import { SubmitFormText, InternalGroupEmails } from '../../helpers/LayoutTexts';
import { ReactComponent as DropDownIcon } from '../../assets/svg/drop_down.svg';
import { DropdownItem } from "../Shared/DropdownItem";
import { LoggedRecruiter } from '../../models/LoggedRecruiter';
import { InputValidation } from "../../models/InputValidation";
import { UserType } from "../../enums/UserType";


interface Props {
    styles: any; 
    recruiter: LoggedRecruiter;
}

export const ContactUs: React.FC<Props>= (props) =>{
    const styles = props.styles;
    const { Alert, openAlert } = useAlert();
    const [typeHovered, setTypeHovered] = useState<boolean>(false);
    const [selectedTeam, setSelectedTeam] = useState<string>(InternalGroupEmails[0].name);
    const [toErrorPage] = useErrorPage();

    const intialFormInputs: RecruiterContactModel = {
        RecruiterName: props.recruiter.forename,
        RecruiterEmail: props.recruiter.email, 
        Attachments: "Upload Attachment",
        ReceiverEmail: InternalGroupEmails[0].email,
        Subject: "",
        Message: ""    
    }
    
    const initialFormValidation: RecruiterContactValidation = {
        Subject: {},
        Message: {}
    }

    const [formInputs, setFormInputs] = useState<RecruiterContactModel>(intialFormInputs);
    const [validation, setValidation] = useState<RecruiterContactValidation>(initialFormValidation);
    const [attachments, setAttachments] = useState<File>();
    const [fileValidation, setFileValidation] = useState<InputValidation>();
    
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setFormInputs((values: any) => ({ ...values, [name]: value }));
        if(validation[name] && validation[name].invalid) {
            validateInput(name, value);
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value);        
    }

    const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setFormInputs((values: any) => ({ ...values, [name]: value }));
        if(validation[name] && validation[name].invalid) {
            validateInput(name, value);
        }
    }

    const handleTextAreaBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value);        
    }

    const handleFileChange = (selectedFiles: FileList) => {
        if (selectedFiles.length > 0) {
            setAttachments(selectedFiles[0]);            
            setFormInputs((values: any) => ({ ...values, ["Attachments"]: selectedFiles[0].name }));
            setFileValidation({valid: true, invalid: false});   
        }
    }

    const validateInput = (name: string, value: string) => {        
        if(value === "") {
            setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: name + " is required"}}));
        } 
        else {
            setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
        } 
    }

    const selectEmail = (value: string ,team: string) => {
        setFormInputs((values: any) => ({ ...values, ["ReceiverEmail"]: value }));
        setSelectedTeam(team);
        setTypeHovered(false);
    }

    const validateForm = (): number => {
        let formValidation: RecruiterContactValidation = {
            Subject: {valid: true},
            Message: {valid: true}
        }        
        if(formInputs.Subject === "") {
            formValidation.Subject = {valid: false, invalid: true, invalidMessage: "Subject is required"};
        }                      
        if(formInputs.Message === "") {
            formValidation.Message = {valid: false, invalid: true, invalidMessage: "Message is required"};
        }        
        setValidation(formValidation);
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key].valid).length;
        return validInputCount;        
    }

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = Object.keys(validation).filter(key => validation[key].valid).length;
        if(validInputCount < 2){
            validInputCount = validateForm();
        }
        if(validInputCount === 2) { 
            const formData = new FormData();
            formData.append("ContactModelString", JSON.stringify(formInputs));
            formData.append("Attchments", attachments!);
            await postRecruiterContactRequest(formData)
            .then((success: boolean) => {
                if(success) {
                    openAlert("success", "Your message has been sent successfully.");
                    setFormInputs(intialFormInputs);
                    setValidation(initialFormValidation);
                }
                else {
                    openAlert("error", "There was a problem with your submission. Please try again later.");
                }
            })
            .catch((error) => {
                toErrorPage(error, UserType.Recruiter);
            });
        }
        else {
            openAlert("error", SubmitFormText.error);
        }
    }
 
    return(
        <> 
            <form style={styles.formWrapper} onSubmit={handleSubmit}>
                <div style={styles.inputWrapper} onMouseEnter={()=>{ setTypeHovered(true); }} onMouseLeave={() =>{ setTypeHovered(false); }}> 
                    <span style={styles.formLabel}> Type </span>
                    <div style={{...styles.selectedWrapper, ...(typeHovered && styles.selectedWrapper.hover)}} onClick={()=>setTypeHovered(!typeHovered)}>
                        <div style={styles.selectedWrapper.text}>{selectedTeam}</div> 
                        <DropDownIcon style={{ ...styles.selectedWrapper.icon, ...(typeHovered && styles.selectedWrapper.icon.hover)}}/>    
                    </div>     
                    {typeHovered && <div style={{...styles.dropdownItems.leave, ...(typeHovered && styles.dropdownItems.hover)}}>
                        {InternalGroupEmails.map((group)=>
                            <DropdownItem key={group.id} styles={styles.dropdownItem} onClick={() => selectEmail(group.email,group.name) } >
                                {group.name}
                            </DropdownItem> 
                        )}
                    </div>}             
                </div>
                <div style={styles.inputWrapper}>
                    <span style={styles.formLabel}> Subject </span>                
                    <TextInput
                        inputType={'text'}
                        name={"Subject"}
                        styles={styles.textInput}
                        value={formInputs.Subject}
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}
                        validation={validation.Subject}
                    /> 
                </div>
                <div style={styles.inputWrapper}>
                    <span style={styles.formLabel}> Any Attachments (Optional) </span> 
                    <FileInput
                        name={'Attachments'}
                        selectedFileName={formInputs.Attachments}
                        styles={styles.attachment}
                        handleChange={(event) => handleFileChange(event!.currentTarget.files!)}
                        validation={fileValidation} 
                    /> 
                </div> 
                <span style={styles.formLabel}> Message </span>
                    <TextAreaInput                
                        name={"Message"}
                        value={formInputs.Message}                                                  
                        handleChange={handleTextAreaChange}
                        handleBlur={handleTextAreaBlur}
                        validation={validation.Message}
                        styles={styles.textAreaInput}
                    />                 
                <Button type="submit" onClick={handleSubmit}  style={styles.submitButton}> {"SUBMIT"} </Button>
            </form>               
            <Alert/>
        </>
    )
}